import { useEffect, useState } from 'react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErroCarregamento, { extrairMensagemErro } from '../../components/common/ErroCarregamento';
import StatusBadge from '../../components/common/StatusBadge';
import Pill from '../../components/common/Pill';
import UsuarioForm from './UsuarioForm';
import { PERFIL_OPCOES } from '../../utils/acreditacaoOpcoes';
import usePaginacao from '../../hooks/usePaginacao';
import useSortable from '../../hooks/useSortable';
import * as usuariosApi from '../../api/usuarios';
import * as hospitaisApi from '../../api/hospitais';

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState(null);
  const [hospitais, setHospitais] = useState([]);
  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [erro, setErro] = useState('');

  const carregar = async () => {
    setErro('');
    try {
      const [resUsuarios, resHospitais] = await Promise.all([usuariosApi.listar(), hospitaisApi.listar()]);
      setUsuarios(resUsuarios.data);
      setHospitais(resHospitais.data);
    } catch (err) {
      setErro(extrairMensagemErro(err, 'Não foi possível carregar os usuários.'));
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const { sorted, Th } = useSortable(usuarios || [], 'nome');
  const { paginados, Paginacao } = usePaginacao(sorted, 15);

  if (!usuarios) return erro ? <ErroCarregamento mensagem={erro} onRetry={carregar} /> : <LoadingSpinner />;

  const nomeHospital = (id) => hospitais.find((h) => h.id_hospital === id)?.nome || '—';
  const labelPerfil = (perfil) => PERFIL_OPCOES.find((p) => p.value === perfil)?.label || perfil;

  const salvar = async (dados) => {
    if (editando) {
      await usuariosApi.atualizar(editando.id_usuario_portal, dados);
    } else {
      await usuariosApi.criar(dados);
    }
    try {
      await carregar();
    } catch {
      // já salvou; só a releitura da lista falhou — não é um erro de salvar
    }
  };

  return (
    <div>
      <PageHeader
        titulo="Usuários"
        subtitulo={`${usuarios.length} usuário(s) cadastrado(s)`}
        acoes={
          <button
            className="am-btn am-btn-dark"
            onClick={() => {
              setEditando(null);
              setModalAberto(true);
            }}
          >
            <span className="ms">person_add</span>
            Novo usuário
          </button>
        }
      />

      <div className="card-soft" style={{ overflow: 'hidden' }}>
        {usuarios.length === 0 ? (
          <div className="empty-state">
            <span className="ms">group</span>
            <div>Nenhum usuário cadastrado ainda.</div>
          </div>
        ) : (
        <div className="am-table-wrap">
          <table className="am-table">
            <thead>
              <tr>
                <Th campo="nome">Nome</Th>
                <Th campo="email">E-mail</Th>
                <Th campo="perfil">Permissão</Th>
                <th>Hospital</th>
                <Th campo="ativo">Status</Th>
                <th />
              </tr>
            </thead>
            <tbody>
              {paginados.map((u) => (
                <tr key={u.id_usuario_portal}>
                  <td style={{ fontWeight: 600 }}>{u.nome}</td>
                  <td>{u.email}</td>
                  <td>
                    <Pill tone={u.perfil === 'cliente' ? 'neutral' : 'dark'}>{labelPerfil(u.perfil)}</Pill>
                  </td>
                  <td>{u.id_hospital ? nomeHospital(u.id_hospital) : '—'}</td>
                  <td>
                    <StatusBadge status={u.ativo ? 'ativo' : 'inativo'} />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="am-btn am-btn-outline"
                      aria-label={`Editar ${u.nome}`}
                      onClick={() => {
                        setEditando(u);
                        setModalAberto(true);
                      }}
                    >
                      <span className="ms" aria-hidden="true">edit</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      <Paginacao />

      {modalAberto && (
        <UsuarioForm
          usuario={editando}
          hospitais={hospitais}
          onClose={() => setModalAberto(false)}
          onSalvar={salvar}
        />
      )}
    </div>
  );
}
